/**
 * ToneScope — a hero's area wears the hero's tone (owner, 19 Oct 2026: D2).
 *
 * Inside it the platform's "primary" family — button fills, checks, radios,
 * the calendar's chosen day, links, hover tints and the focus ring — is the
 * tone's (the `.tone-scope-<tone>` classes in @tonnie/design-tokens), so every
 * control follows without a `tone` prop of its own.
 *
 * Dialogs, menus, popovers and selects render in a portal, outside the page's
 * DOM: a class on the page would never reach them. React context does cross a
 * portal, so each overlay's content reads `useToneScopeClass()` and wears the
 * same class — a dialog opened from a hero is in the hero's tone wherever it
 * renders.
 *
 * `tone="platform"` puts the platform's own colour back inside a hero, for an
 * action that belongs to another product (CoPilot, the app) — D3.
 */
import type { Tone } from '@tonnie/design-tokens';
import { createContext, useContext, type ReactNode } from 'react';

import { cn } from './utils';

export type ToneScopeTone = Tone | 'platform';

const SCOPE_CLASS: Record<ToneScopeTone, string> = {
  green: 'tone-scope-green',
  amber: 'tone-scope-amber',
  orange: 'tone-scope-orange',
  red: 'tone-scope-red',
  rose: 'tone-scope-rose',
  blue: 'tone-scope-blue',
  cyan: 'tone-scope-cyan',
  violet: 'tone-scope-violet',
  slate: 'tone-scope-slate',
  platform: 'tone-scope-platform',
};

const ToneScopeContext = createContext<ToneScopeTone | null>(null);

/** The scope's class, for an overlay's content — undefined outside any scope. */
export function useToneScopeClass(): string | undefined {
  const tone = useContext(ToneScopeContext);
  return tone ? SCOPE_CLASS[tone] : undefined;
}

export interface ToneScopeProps {
  tone: ToneScopeTone;
  children: ReactNode;
  className?: string;
  /** `contents`: the scope adds no box of its own (the default); `block` for a wrapper that lays out. */
  display?: 'contents' | 'block';
  'data-testid'?: string;
}

export function ToneScope({
  tone,
  children,
  className,
  display = 'contents',
  'data-testid': testId,
}: ToneScopeProps) {
  return (
    <ToneScopeContext.Provider value={tone}>
      <div
        className={cn(SCOPE_CLASS[tone], display === 'contents' && 'contents', className)}
        data-tone-scope={tone}
        data-testid={testId}
      >
        {children}
      </div>
    </ToneScopeContext.Provider>
  );
}
